import React, { useState } from "react";
import config from "./config.json";
import axios from "axios";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Rating from "@mui/material/Rating";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Grid from "@mui/material/Grid";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Tooltip from "@mui/material/Tooltip";
import RemoveIcon from "@mui/icons-material/Remove";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";

import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import "./styles.css";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";

import { useTheme, useMediaQuery } from "@mui/material";
import Link from "@mui/material/Link";
import AppContext from "./AppContext";
import AnswerNote from "./AnswerNote";
import { Episode, filterEpisodes } from "./ChatPage";

interface ChatPromptProps {
  scoreThreshold: number;
  useOnlyTranscriptsFlag: boolean;
  setUseOnlyTranscriptsFlag: (flag: boolean) => void;
  historyRetention: number;
  setHistoryRetention: (val: number) => void;
  listTextChunksFlag: boolean;
  episodes: Episode[];
  selectedEpisodes: Episode[];
  setSelectedEpisodes: (eps: Episode[]) => void;
}

interface UserQuestion {
  text: string;
  index: number;
}

interface Vote {
  index: number;
  rating: number;
  feedback: string;
  question: string;
  answer: string;
  scoreThreshold: number;
  useOnlyTranscriptsFlag: boolean;
  historyRetention: number;
  questionCategory: string;
  embeddedQuestion: string;
  userEmail?: string;
  cognitoUsername?: string;
  cognitoGroups?: string[];
  isFollowUp: number;
}

// Categories where the backend constructs the answer string directly (no LLM
// formatting step), so the response shape is fixed regardless of how the user
// phrased the request. Categories not listed here go through the LLM and
// respect free-form formatting requests.
const FIXED_FORMAT_CATEGORIES: Record<string, string> = {
  E: "Topic-search results are returned as a ranked list of episodes with their reference links and transcript timestamps.",
  F: "Cross-references between episodes are returned as a bullet list with episode number, title, recording date, and guest.",
  I: "Guest-appearance lookups return a plain list of episodes featuring that guest.",
  J: "Mini-series queries return the canonical list of episodes belonging to that series.",
};

const fixedFormatNotice = (category: string | undefined | null) =>
  (category && FIXED_FORMAT_CATEGORIES[category]) || null;

// Phrases the bot uses (or paraphrases) when it can't ground an answer in the
// show's content. We look for these near the START of the answer, because
// refusals lead with the disclaimer; substantive answers may mention similar
// phrases mid-paragraph as part of normal prose.
const NO_ANSWER_REGEX = new RegExp(
  [
    "i\\s*don'?t\\s+know",
    "i\\s+do\\s+not\\s+know",
    "i\\s*don'?t\\s+have\\s+information\\s+about",
    "i\\s*don'?t\\s+have\\s+(any|the)\\s+(information|details|data)\\s+about",
    "i\\s*can(no|')t\\s+(find|locate|determine|identify|answer)",
    "i\\s*couldn'?t\\s+find",
    "i\\s+am\\s+(unable|not\\s+able)\\s+to",
    "i'?m\\s+(unable|not\\s+able)\\s+to",
    "no\\s+(information|mention|reference|details)\\s+(about|on|of|regarding|covering)",
    "(is|are)\\s+not\\s+(mentioned|covered|discussed|available)",
    "(does|do)\\s*n'?t\\s+(cover|mention|discuss|contain)",
    "not\\s+available\\s+in\\s+(your|my|the|our)\\s+(training|knowledge|context)",
    "this\\s+topic\\s+is\\s+not\\s+mentioned",
    "(has\\s+not|hasn'?t)\\s+(yet\\s+)?been\\s+released",
    "(is|has)\\s+beyond\\s+my\\s+(current\\s+)?(knowledge|training)",
  ].join("|"),
  "i"
);

// The backend appends this header before the list of retrieved sources. If
// it's present, the bot found relevant content — even if it declined to
// synthesize an answer, the user has episodes to investigate, and the
// "couldn't find an answer" note would be misleading.
const REFERENCES_MARKER = /this\s+result\s+was\s+based\s+on\s+the\s+following:/i;

const looksLikeNoAnswer = (text: string | undefined) => {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  // If references were retrieved, suppress the note — the user has somewhere
  // to look, even if the LLM hedged on the synthesis.
  if (REFERENCES_MARKER.test(trimmed)) return false;
  // Otherwise, look for a refusal phrase near the opening — refusals lead
  // with it; substantive answers don't.
  const opening = trimmed.slice(0, 250);
  return NO_ANSWER_REGEX.test(opening);
};

const exampleQuestions = [
  "What types of topics are discussed in the Energy Transition Show?",
  "What challenges do VPPs face? Give me details",
  "Can you give me a bullet list of tags for episode 201?",
  "Give me a summary for episode 201",
  "Are there conflicting opinions in episode 270?",
  "What challenges do VPPs face as discussed in episode 197?",
  "Which episodes discuss Virtual Power Plants (VPP)?",
  "Which episodes are mentioned in episode 201?",
  "Which guests have appeared on the show the most?",
  "Could you provide a summary of the news segment in Episode 201?",
  "Which episodes feature Mohua Mukherjee as a guest?",
  "What are all episode numbers of the Climate Science mini-series?"
];

const ChatPrompt: React.FC<ChatPromptProps> = ({ 
  scoreThreshold, 
  useOnlyTranscriptsFlag, 
  setUseOnlyTranscriptsFlag,
  historyRetention,
  setHistoryRetention,
  listTextChunksFlag,
  episodes,
  selectedEpisodes,
  setSelectedEpisodes
}) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const appContext = React.useContext(AppContext);

  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [userQuestions, setUserQuestions] = useState<UserQuestion[]>([]);
  const [logMessage, setLogMessage] = useState<string | null>(null);
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const [currentFeedbackIndex, setCurrentFeedbackIndex] = useState<number | null>(null);
  const [currentRating, setCurrentRating] = useState<number | null>(null);
  const [currentFeedbackText, setCurrentFeedbackText] = useState("");

  // Settings Popover State
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);
  const openPopover = Boolean(anchorEl);

  // Filter Popover State
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleFilterOpen = (event: React.MouseEvent<HTMLButtonElement>) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);
  const openFilterPopover = Boolean(filterAnchorEl);

  const mobileRelativeSize = "0.6rem !important";
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (!appContext) {
    return <Typography>Loading chat...</Typography>;
  }

  const {
    chatMessages,
    setChatMessages,
    chatHistory,
    setChatHistory,
    submittedFeedback,
    setSubmittedFeedback
  } = appContext;

  const handleOpenFeedbackDialog = (index: number, rating: number) => {
    setCurrentFeedbackIndex(index);
    setCurrentRating(rating);
    setOpenFeedbackDialog(true);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setLogMessage("Response copied to clipboard!");
    setTimeout(() => setLogMessage(null), 3000);
  };

  const handleCloseFeedbackDialog = () => {
    setOpenFeedbackDialog(false);
    setCurrentRating(null);
    setCurrentFeedbackText("");
    setCurrentFeedbackIndex(null);
  };

  const handleSubmitFeedback = async () => {
    const userApiKey = auth.user?.profile?.['custom:user_api_key'] as string;
    if (!auth.user?.id_token) {
      setLogMessage("Error: You must be logged in to submit feedback.");
      setTimeout(() => setLogMessage(null), 3000);
      handleCloseFeedbackDialog();
      return;
    }
    if (currentFeedbackIndex !== null && currentRating !== null) {
      const questionText = chatMessages[currentFeedbackIndex - 1]?.text || "";
      const answer = chatMessages[currentFeedbackIndex]?.text || "";
      const questionCategory = chatMessages[currentFeedbackIndex]?.questionCategory || "";
      const embeddedQuestion = chatMessages[currentFeedbackIndex]?.embeddedQuestion || "";
      const isFollowUp = chatMessages[currentFeedbackIndex]?.isFollowUp || 0;
      
      const newVote: Vote = {
        index: currentFeedbackIndex,
        rating: currentRating,
        feedback: currentFeedbackText,
        question: questionText,
        answer,
        scoreThreshold,
        useOnlyTranscriptsFlag,
        historyRetention,
        questionCategory: questionCategory,
        embeddedQuestion: embeddedQuestion,
        userEmail: auth.user?.profile?.email,
        cognitoUsername: (auth.user?.profile?.["cognito:username"] as string | undefined) ||
                         (auth.user?.profile?.preferred_username as string | undefined) ||
                         (auth.user?.profile?.username as string | undefined),
        cognitoGroups: auth.user?.profile?.["cognito:groups"] as string[] | undefined,
        isFollowUp: isFollowUp, 
      };
  
      setVotes((prevVotes) => [...prevVotes, newVote]);
  
      try {
        const logVoteUrl = `${config.api.chatbot.url}/log-vote`;
        await axios.post(logVoteUrl, newVote, {
          headers: {
            'Authorization': auth.user?.id_token,
            'X-Api-Key': userApiKey,
          },
          timeout: config.timeouts.chatApi,
        });
        setSubmittedFeedback((prev) => [...prev, currentFeedbackIndex]);
        setLogMessage("Feedback logged successfully.");
        setTimeout(() => setLogMessage(null), 3000);
      } catch (error) {
        setLogMessage("Error logging feedback.");
        setTimeout(() => setLogMessage(null), 3000);
      }
  
      handleCloseFeedbackDialog();
    }
  };

  const formatResponse = (response: any, currentPromptText: string) => response.replace(currentPromptText, "");

  const sendMessage = async (messageText: string) => {
    const userApiKey = auth.user?.profile?.['custom:user_api_key'] as string;
    if (!auth.user?.id_token) {
      setApiError("Authentication error: Please log in to use the chat.");
      setChatMessages((prevMessages) => [...prevMessages, { text: "Authentication error: Please log in to use the chat.", isBot: true }]);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      const chatbotUrl = `${config.api.chatbot.url}/complete`;
      
      const requestPayload = {
        prompt: messageText, // Send the clean, original prompt
        scoreThreshold,
        useOnlyTranscriptsFlag,
        historyRetention,
        listTextChunksFlag,
        history: chatHistory,
        // Map the selected episodes to an array of just their codes (e.g., ["197", "201"])
        selected_episodes: selectedEpisodes.map(ep => ep.code), 
        userEmail: auth.user?.profile?.email,
        cognitoUsername: auth.user?.profile?.["cognito:username"] || auth.user?.profile?.preferred_username || auth.user?.profile?.username,
        cognitoGroups: auth.user?.profile?.["cognito:groups"],
      };

      console.log("DEBUG: Sending payload to backend:", requestPayload);

      const response = await axios.post(chatbotUrl, requestPayload, {
        headers: {
          'Authorization': auth.user?.id_token,
          'X-Api-Key': userApiKey,
        },
        timeout: config.timeouts.chatApi,
      });
  
      const result = response.data;
      const botReply = {
        text: formatResponse(result.response, messageText),
        isBot: true,
        chartImage: result.chartImage || null, 
        questionCategory: result.question_category || null,
        embeddedQuestion: result.embedded_question || null,
        isFollowUp: result.is_question_a_follow_up_question || 0,
      };
      setChatMessages((prevMessages) => [...prevMessages, botReply]);
      setChatHistory(result.history);
      setApiError(null);
    } catch (error: any) {
      console.error("Error:", error);
      const status = error?.response?.status;
      if (status === 429) {
        setApiError("You've reached your daily request limit. Please try again tomorrow.");
      } else if (status === 403) {
        setApiError("Access denied. Your API key may be invalid or expired.");
      } else {
        setApiError("Unable to reach the server. Please check your connection and try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e?: React.FormEvent, textOverride?: string) => {
    if (e) e.preventDefault();
    setApiError(null);
    
    const textToSend = textOverride || prompt;
    if (textToSend.trim()) {
      setChatMessages((prevMessages) => [...prevMessages, { text: textToSend, isBot: false }]);
      setUserQuestions((prevQuestions) => [...prevQuestions, { text: textToSend, index: chatMessages.length }]);
      sendMessage(textToSend);
      setPrompt("");
    }
  };

  const handleInputChange = (e: any) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (!isLoading) {
        e.preventDefault();
        handleSubmit();
      }
    } else {
      setPrompt(e.target.value);
    }
  };

  return (
    <Stack direction="column" sx={{ height: "100%", width: "100%", overflow: "hidden", ...(isSmallScreen && { "& *:not(h4)": { fontSize: mobileRelativeSize } }) }}>
      
      {/* Scrollable Chat Area - Centered Flex Container */}
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, overflowY: "auto", pb: 4, display: "flex", justifyContent: "center" }}>
        
        {/* Centralized 800px Column */}
        <Box sx={{ width: "100%", maxWidth: "800px", display: "flex", flexDirection: "column" }}>
          
          {/* Title */}
          <Typography 
            variant="h4"
            align="center"
            sx={{ mb: 4, ...(isSmallScreen && { fontSize: "1.3rem !important" }) }}
          >
            The Energy Transition Show - Q&A
          </Typography>

          {/* Modern Empty State */}
          {chatMessages.length === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, textAlign: 'center', color: "text.secondary" }}>
                Select and adapt a sample question from below, or type your own
              </Typography>
              <Typography variant="body2" sx={{ mb: 2.5, textAlign: 'center', color: "text.secondary" }}>
                Ask in English, German, French, Spanish, or many other languages.
                {' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/faq')}
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 600,
                    verticalAlign: 'baseline',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  New here? See the FAQ
                </Link>
              </Typography>
              <Grid container spacing={2}>
                {exampleQuestions.map((ex, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        borderRadius: 3, 
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderColor: 'primary.light',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        } 
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
                        <CardActionArea onClick={() => handleSubmit(undefined, ex)} sx={{ p: 2, flex: 1 }}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                            {ex}
                          </Typography>
                        </CardActionArea>
                        <Tooltip title="Copy to input box to edit before sending">
                          <IconButton
                            size="small"
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setPrompt(ex); }}
                            sx={{ alignSelf: 'center', mr: 1 }}
                            aria-label="Copy question to input"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Message Bubbles */}
          <Stack spacing={4} sx={{ width: "100%", mt: 2 }}>
            {chatMessages.map((message, index) =>
              message.isBot ? (
                // BOT MESSAGE: Takes full width of the 800px column, no colored background
                <Box key={index} sx={{ alignSelf: "flex-start", width: "100%" }}>
                  <Typography
                    component="div"
                    sx={{
                      width: "100%",
                      color: "text.primary",
                      "& .markdown-container p": { marginTop: 0, marginBottom: "0.5em" },
                      "& .markdown-container h1, & .markdown-container h2, & .markdown-container h3, & .markdown-container h4": {
                        marginTop: "1em",
                        marginBottom: "0.5em",
                        fontWeight: "bold",
                        fontSize: "1.2rem"
                      },
                      "& .markdown-container ul, & .markdown-container ol": {
                        marginTop: 0,
                        marginBottom: "0.5em",
                        paddingLeft: "1.5em"
                      },
                      "& .markdown-container > *:first-child": { marginTop: "0 !important", paddingTop: "0 !important" },
                      "& .markdown-container > *:last-child": { marginBottom: "0 !important" },
                      "& .markdown-container p:empty": { display: "none" },
                      "& .markdown-container li:empty": { display: "none" },
                    }}
                  >
                    {message.chartImage && (
                      <Box sx={{ marginBottom: "1em" }}>
                        <img src={message.chartImage} alt="Chart" style={{ maxWidth: "100%", borderRadius: "8px", display: "block", mixBlendMode: "multiply" }} />
                      </Box>
                    )}
                    {(() => {
                      const explanation = fixedFormatNotice(message.questionCategory);
                      if (explanation) {
                        return (
                          <AnswerNote
                            title="Standard format applied."
                            faqAnchor="standard-format"
                            body={
                              <>
                                {explanation} We keep the layout consistent for this
                                question type to avoid fabricating fields the source
                                material doesn't cover.
                              </>
                            }
                          />
                        );
                      }
                      if (looksLikeNoAnswer(message.text)) {
                        return (
                          <AnswerNote
                            title="It looks like the bot couldn't find an answer."
                            faqAnchor="answer-quality"
                            body={
                              <>
                                This is by design — the bot only answers from the show's
                                content. It won't invent information and it can't access
                                the internet. If you expected an answer here, see the FAQ
                                for things to check.
                              </>
                            }
                          />
                        );
                      }
                      return null;
                    })()}
                    <Box className="markdown-container" sx={{ mt: 0, pt: 0 }}>
                      <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkBreaks]}>
                        {message.text?.trim()}
                      </ReactMarkdown>
                    </Box>
                  </Typography>
                  
                  {message.isBot && index !== 0 && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, color: "text.secondary" }}>
                      {submittedFeedback.includes(index) ? (
                        <Typography sx={{ color: "success.main", fontSize: "0.85rem", alignSelf: "center", mr: 1, fontWeight: 500 }}>
                          Feedback logged!
                        </Typography>
                      ) : (
                        <>
                          <Tooltip title="Good response">
                            <IconButton size="small" onClick={() => handleOpenFeedbackDialog(index, 5)}>
                              <ThumbUpOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Bad response">
                            <IconButton size="small" onClick={() => handleOpenFeedbackDialog(index, 1)}>
                              <ThumbDownOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Copy response">
                        <IconButton size="small" onClick={() => handleCopy(message.text)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
              ) : (
                // USER MESSAGE: Aligned to the right, soft grey/blue bubble
                <Typography
                  key={index}
                  sx={{
                    alignSelf: "flex-end",
                    padding: "12px 20px",
                    borderRadius: "24px",
                    backgroundColor: "action.hover",
                    color: "text.primary",
                    maxWidth: { xs: "90%", md: "75%" },
                    fontWeight: 400
                  }}
                >
                  {message.text}
                </Typography>
              )
            )}
            
            {/* Typing Indicator */}
            {isLoading && (
              <Box sx={{ alignSelf: "flex-start", width: "100%", color: "text.secondary", fontStyle: "italic" }}>
                <Typography className="typing-indicator" sx={{ background: "transparent", padding: 0 }}>
                  Generating response
                </Typography>
              </Box>
            )}
          </Stack>

          {/* System Alerts */}
          {logMessage && (
            <Alert severity={logMessage.includes("Error") ? "error" : "success"} sx={{ mt: 2, borderRadius: 2 }}>
              {logMessage}
            </Alert>
          )}
          {apiError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {apiError}
            </Alert>
          )}
        </Box>
      </Box>

      {/* Sleek Input Box Area fixed at bottom */}
      <Box sx={{ p: 2, pb: 3, backgroundColor: 'background.paper', flexShrink: 0, zIndex: 10, position: 'relative' }}>
        
        {/* The 800px Central Column for the Input Box */}
        <Box sx={{ maxWidth: "800px", margin: "0 auto", display: "flex", flexDirection: "column", gap: 1 }}>
          
          {/* Active Filter Chips */}
          {selectedEpisodes.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedEpisodes.map(ep => (
                <Chip 
                  key={ep.code}
                  label={`Filtering: ${ep.name}`}
                  onDelete={() => setSelectedEpisodes(selectedEpisodes.filter(e => e.code !== ep.code))} 
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ backgroundColor: "primary.50" }}
                />
              ))}
            </Box>
          )}

          {/* Input Area */}
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              p: "8px 12px",
              borderRadius: "24px",
              backgroundColor: "action.hover",
              border: "1px solid",
              borderColor: "divider"
            }}
          >
            <IconButton onClick={handlePopoverOpen} sx={{ color: 'text.secondary' }}>
              <AddIcon />
            </IconButton>

            {/* Filter Icon */}
            <Tooltip title="Filter by Episode">
              <IconButton 
                onClick={handleFilterOpen} 
                sx={{ color: selectedEpisodes.length > 0 ? 'primary.main' : 'text.secondary' }}
              >
                <FilterAltOutlinedIcon />
              </IconButton>
            </Tooltip>
            
            <InputBase
              sx={{ ml: 1, flex: 1, p: '4px', '& .MuiInputBase-input': { fontSize: '16px !important', lineHeight: '1.5 !important' } }}
              placeholder="Ask anything..."
              multiline
              maxRows={6}
              value={prompt}
              onChange={handleInputChange}
              onKeyDown={handleInputChange}
            />

            <IconButton 
              color="primary" 
              sx={{ p: 1, m: 0.5, backgroundColor: prompt.trim() ? 'primary.main' : 'action.disabledBackground', color: 'white', '&:hover': { backgroundColor: prompt.trim() ? 'primary.dark' : 'action.disabledBackground' } }} 
              disabled={isLoading || !prompt.trim()} 
              onClick={handleSubmit}
            >
              <SendIcon />
            </IconButton>
          </Paper>
        </Box>

        {/* Quantum Analytics Logo - Absolutely positioned to the bottom right of the entire page */}
        <Box sx={{ position: "absolute", bottom: 24, right: 24, zIndex: 20, display: { xs: "none", sm: "block" } }}>
          <a href="https://quantumanalytics.ch/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}>
            <img
              src="/q-logo.png"
              alt="Quantum Analytics Logo"
              height="60px"
              style={{ display: 'block', borderRadius: '8px', padding: '4px', backgroundColor: 'white' }}
            />
          </a>
        </Box>
        
      </Box>

      {/* Searchable Episode Filter Popover */}
      <Popover open={openFilterPopover} anchorEl={filterAnchorEl} onClose={handleFilterClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ mb: 1 }}>
        <Box sx={{ p: 2, width: { xs: "280px", sm: "350px" } }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>Select Episode(s)</Typography>
          <Autocomplete<Episode, true>
            multiple
            value={selectedEpisodes}
            onChange={(event, newValue) => setSelectedEpisodes(newValue)}
            options={episodes || []}
            getOptionLabel={(option) => option.name}
            filterOptions={(options, { inputValue }) => filterEpisodes(options, inputValue)}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => <TextField {...params} label="Search episodes by name, topic, guest, series..." size="small" autoFocus />}
            ListboxProps={{ style: { maxHeight: "250px" } }}
          />
        </Box>
      </Popover>

      {/* Modern Popover menu for Advanced Options */}
      <Popover open={openPopover} anchorEl={anchorEl} onClose={handlePopoverClose} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }} sx={{ mb: 1, ml: -1 }}>
        <Box sx={{ p: 2, minWidth: "250px" }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>Chat Settings</Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" color="textSecondary">Exclude reference links</Typography>
              <Tooltip title="By default, answers draw on show material (transcripts, news, FAQ, descriptions) plus external reference links. When active, external reference links are excluded." placement="top" arrow>
                <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "text.secondary", cursor: "pointer" }} />
              </Tooltip>
            </Stack>
            <Switch size="small" checked={useOnlyTranscriptsFlag} onChange={(e) => setUseOnlyTranscriptsFlag(e.target.checked)} sx={{ mr: -1 }} />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" color="textSecondary">Memory</Typography>
              <Tooltip title="This sets the number of chat questions retained in history." placement="top" arrow>
                <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "text.secondary", cursor: "pointer" }} />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center">
              <IconButton size="small" onClick={() => setHistoryRetention(Math.max(0, historyRetention - 1))} disabled={historyRetention <= 0}><RemoveIcon fontSize="small" /></IconButton>
              <Typography variant="body2" sx={{ fontWeight: 'bold', width: '20px', textAlign: 'center' }}>{historyRetention}</Typography>
              <IconButton size="small" onClick={() => setHistoryRetention(Math.min(10, historyRetention + 1))} disabled={historyRetention >= 10}><AddIcon fontSize="small" /></IconButton>
            </Stack>
          </Stack>
        </Box>
      </Popover>

      {/* Feedback Dialog */}
      <Dialog open={openFeedbackDialog} onClose={handleCloseFeedbackDialog}>
        <DialogTitle>Provide Feedback</DialogTitle>
        <DialogContent>
          <Typography component="legend">Rate your answer:</Typography>
          <Rating value={currentRating} onChange={(_, newValue) => setCurrentRating(newValue)} precision={1} />
          <TextField label="Additional Feedback" multiline rows={4} value={currentFeedbackText} onChange={(e) => setCurrentFeedbackText(e.target.value)} sx={{ marginTop: 2, width: "100%" }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedbackDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitFeedback} disabled={currentRating === null}>Submit</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ChatPrompt;